import React, { useEffect, useRef, useState } from 'react';
import { useLocation, Redirect, useHistory } from 'react-router-dom'; // add Redirect
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import claimService from 'src/services/claimService';
import ClaimDetailsView from '../ClaimDetailView';
import Header from './Header';
import { ThemeProvider, useTheme, makeStyles } from '@material-ui/styles';
import Page from 'src/components/Page';
import { Box, Container, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField } from '@material-ui/core';
import Filter from './Filter';
import Results from './Results';
import externalClaimsTheme from '../externalClaimTheme';
import { externalLogin } from 'src/actions/accountActions';
import { updateMember } from 'src/actions/claimActions';


const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: '100%',
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(3)
  },
  header: {
    position: 'sticky',
    top: 0,
    zIndex: 1000,
    backgroundColor: theme.palette.background.dark,
  },
}));

function GlassOrderLandingPage() {
  const { t } = useTranslation();
  const summaryRef = useRef();
  const location = useLocation();
  const [claims, setClaims] = useState([]);
  const [haveAllClaimsLoaded, setHaveAllClaimsLoaded] = useState(false);
  const [isLoadingClaims, setIsLoadingClaim] = useState(true);
  const [filterCriteria, setFilterCriteria] = useState({});
  const [summaryView, setSummaryView] = useState('grid');
  const [summaryPage, setSummaryPage] = useState(1);
  const [scrollLocation, setScrollLocation] = useState(0);
  const claimId = new URLSearchParams(location.search).get('claimId');
  const fromEmail = new URLSearchParams(location.search).get('fromEmail');
  const [vendorDealerCode] = useState(location.state?.dealerCode || '');
  const [trackingNumber] = useState(location.state?.trackingNumber || '');
  const [fromGlassSearchView] = useState(location.state?.fromGlassSearchView);
  const [userEmail] = useState(location.state?.userEmail || '');
  const [userName] = useState(location.state?.userName || '');
  const theme = useTheme();
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const isFromEmail = !!fromEmail;
  const hasSearchViewData = fromGlassSearchView && vendorDealerCode && trackingNumber;

  const handleChangeSummaryView = (newMode) => {
    setSummaryView(newMode);
  }

  const handleChangeSummaryPage = (newPage) => {
    setSummaryPage(newPage);
  }

  const handleChangeIsSummaryScrolling = (scrollPosition) => {
    setScrollLocation(scrollPosition);
  }

  const updateFilterCriteria = data => {
    setFilterCriteria(filterCriteria => ({
      ...filterCriteria,
      ...data
    }));

    if (data?.allFilters?.length !== filterCriteria?.allFilters?.length) {
      setSummaryPage(1);
    }
  };

  useEffect(() => {
    if (fromGlassSearchView) {
      claimService.cancelRequest();
      setHaveAllClaimsLoaded(false);
      setIsLoadingClaim(true);
      collectClaims();
    }
  }, []);

  useEffect(() => {
    if (summaryRef.current) {
      // preserve summary view scroll position
      summaryRef.current.parentElement.scrollTo(0, scrollLocation);
    }
  }, [location.search]);


  /**
 * @param {boolean} isNewClaim 
 */
  const handleGoBackToSummary = (isNewClaim) => {
    if (isNewClaim) {
      claimService.cancelRequest();
      handleCancelClaimRequest();
    }
  }

  /**
   * @param {{
   *  claimId: number, 
   *  attachments: {files: object[]}[], 
   *  detail: {disposition: string}
   * }} updatedClaim 
   */
  const handleClaimUpdate = (updatedClaim) => {
    let targetClaimIndex = 0;
    const claimsCopy = [...claims];
    const newDocumentCount = updatedClaim.documentCount;
    const targetClaim = claimsCopy.find((claim, index) => {
      targetClaimIndex = index;
      return claim.claimId == updatedClaim.claimId;
    });
    const targetClaimCopy = { ...targetClaim };

    claimsCopy[targetClaimIndex] = {
      ...targetClaimCopy,
      documentCount: newDocumentCount,
    };

    setClaims(claimsCopy);
  }

  const handleCancelClaimRequest = () => {
    setHaveAllClaimsLoaded(false);
    setIsLoadingClaim(true);
    collectClaims();
  }

  const collectClaims = async () => {
    const dealerCode = vendorDealerCode;
    if (dealerCode) {
      try {
        const initialResponse = await claimService.getVendorClaims({
          dealerCode,
          trackingNumber
        });

        setClaims(initialResponse.claims);

        setHaveAllClaimsLoaded(true);
        setIsLoadingClaim(false);
      } catch (error) {
        if (claimService.isCancelError(error)) {
          handleCancelClaimRequest();
        }
      }
    }
  };


  const handleClaimCardClick = (claim) => {
    if (claim?.claimId) {
      history.push({
        pathname: '/glass/view-claim', // reloads the same page
        search: `?claimId=${claim.claimId}`,
        state: {
          userEmail: userEmail,
          userName: userName,
          fromGlassSearchView: true,
          dealerCode: vendorDealerCode,
          trackingNumber: trackingNumber,
        }
      });
    }
  };



  // Handler to ensure dealer/order info is set before filing a new claim
  const handleFileNewGlassClaim = async () => {
    if (!fromGlassSearchView || !vendorDealerCode || !trackingNumber) {
      enqueueSnackbar(t('glassSearch.missingDealerShipper'), { variant: 'warning' });
      return;
    }
    const state = history.location.state || {};
    const hasDealerInfo = !!state.dealerInfo;
    const hasOrderInfo = !!state.orderInfo;

    if (!hasDealerInfo || !hasOrderInfo) {
      try {
        const { dealerInfo, orderInfo } = await dispatch(externalLogin(vendorDealerCode, trackingNumber));
        if (dealerInfo && orderInfo) {
          await dispatch(updateMember({ dealerInfo, orderInfo }));
          history.push({
            pathname: '/glass/claim',
            state: {
              fromGlassSearchView: true,
              dealerCode: vendorDealerCode,
              trackingNumber: trackingNumber,
              dealerInfo,
              orderInfo
            }
          });
          return;
        }
      } catch (error) {
        enqueueSnackbar(t('glassSearch.fetchDealerOrderError'), { variant: 'error' });
      }
    }
    // If already have dealerInfo/orderInfo, or fetch failed, just navigate and preserve what we have
    history.push({
      pathname: '/glass/claim',
      state: {
        fromGlassSearchView: true,
        dealerCode: vendorDealerCode,
        trackingNumber: trackingNumber,
        ...(state.dealerInfo ? { dealerInfo: state.dealerInfo } : {}),
        ...(state.orderInfo ? { orderInfo: state.orderInfo } : {})
      }
    });
  };

  if (!(hasSearchViewData || isFromEmail || claimId)) {
    return <Redirect to="/glass/search" />;
  }




  if (claimId) {
    return (
      <ClaimDetailsView
        claimId={claimId}
        onGoBack={handleGoBackToSummary}
        onClaimUpdate={handleClaimUpdate}
      />
    );
  }

  return (
    <ThemeProvider theme={externalClaimsTheme(theme)}>
      <Page ref={summaryRef} className={classes.root} title={t('glassClaims')}>
        <Container maxWidth="lg">
          <Box component="header" className={classes.header}>
            <Header
              fromGlassSearchView={fromGlassSearchView}
              title={t('viewClaimsForOrder', { trackingNumber })}
              onFileNewGlassClaim={handleFileNewGlassClaim}
            />
            <Filter criteria={filterCriteria} onUpdate={updateFilterCriteria} />
          </Box>

          <Results
            claims={claims}
            filterCriteria={filterCriteria}
            isLoading={isLoadingClaims}
            haveAllClaimsLoaded={haveAllClaimsLoaded}
            view={summaryView}
            onViewChange={handleChangeSummaryView}
            page={summaryPage}
            onPageChange={handleChangeSummaryPage}
            onScroll={handleChangeIsSummaryScrolling}
            scrollLocation={scrollLocation}
            cardClickHandler={handleClaimCardClick}
          />

        </Container>
      </Page>
    </ThemeProvider >


  );
}

export default GlassOrderLandingPage = React.memo(GlassOrderLandingPage);
